"use client";

import { cva } from "class-variance-authority";
import type { ElementType, FC, HTMLAttributes, ReactNode } from "react";
import { Link, type LinkProps } from "@/components/ui/link";
import { cn } from "@/lib/utils";
import type { Layout } from "@/types";
import {
  TitleHeadingContext,
  type TitleHeadingContextValue,
  useTitleHeading,
} from "./title-heading.context";

export type TitleHeadingLayout = Extract<Layout, "desktop" | "mobile">;
export type TitleHeadingSize = "sm" | "md" | "lg" | "xl";
export type TitleHeadingTitleColor =
  | "strong"
  | "heading"
  | "default"
  | "muted"
  | "primary"
  | "accent"
  | "on-dark"
  | "cat-saude"
  | "cat-educacao"
  | "cat-trabalho";
export type TitleHeadingMarkerColor =
  | "accent"
  | "primary"
  | "strong"
  | "on-dark"
  | "cat-saude"
  | "cat-educacao"
  | "cat-trabalho";

export interface TitleHeadingProps extends HTMLAttributes<HTMLElement> {
  layout?: TitleHeadingLayout;
  size?: TitleHeadingSize;
  titleColor?: TitleHeadingTitleColor;
  markerColor?: TitleHeadingMarkerColor;
  as?: ElementType;
}

const ROOT_VARIANTS = cva("flex items-center", {
  variants: {
    layout: {
      desktop: "gap-2.5",
      mobile: "flex-wrap gap-2",
    },
  },
});

const Root: FC<TitleHeadingProps> = ({
  layout = "desktop",
  size = "md",
  titleColor = "strong",
  markerColor = "accent",
  as: Tag = "div",
  className,
  children,
  ...props
}) => {
  const contextValue: TitleHeadingContextValue = { layout, size, titleColor, markerColor };

  return (
    <TitleHeadingContext.Provider value={contextValue}>
      <Tag data-layout={layout} className={cn(ROOT_VARIANTS({ layout }), className)} {...props}>
        {children}
      </Tag>
    </TitleHeadingContext.Provider>
  );
};

interface PartProps {
  className?: string;
}

const MARKER_VARIANTS = cva("shrink-0 rounded-pill", {
  variants: {
    size: {
      sm: "h-4 w-1.5",
      md: "h-5 w-1.5",
      lg: "h-6 w-1.5",
      xl: "h-9 w-2",
    },
    color: {
      accent: "bg-accent",
      primary: "bg-primary",
      strong: "bg-fg-strong",
      "on-dark": "bg-fg-on-dark",
      "cat-saude": "bg-cat-saude",
      "cat-educacao": "bg-cat-educacao",
      "cat-trabalho": "bg-cat-trabalho",
    },
  },
});

const Marker: FC<PartProps> = ({ className }) => {
  const { size, markerColor } = useTitleHeading();

  return (
    <span aria-hidden className={cn(MARKER_VARIANTS({ size, color: markerColor }), className)} />
  );
};

interface TitleProps extends PartProps {
  /** `id` of the heading; the surrounding `<section>` points to it in `aria-labelledby`. */
  id: string;
  /** Semantic heading level. It does not change the look: `size` on the root does. */
  level?: 1 | 2 | 3 | 4 | 5 | 6;
  children: ReactNode;
}

const TITLE_VARIANTS = cva("font-display font-bold", {
  variants: {
    size: {
      sm: "text-body-lg",
      md: "text-h6",
      lg: "text-h5",
      // Display Bold 36/44 — the 3xl style from Figma.
      xl: "text-h3",
    },
    color: {
      strong: "text-fg-strong",
      heading: "text-fg-heading",
      default: "text-fg-default",
      muted: "text-fg-muted",
      primary: "text-primary",
      accent: "text-accent",
      "on-dark": "text-fg-on-dark",
      "cat-saude": "text-cat-saude",
      "cat-educacao": "text-cat-educacao",
      "cat-trabalho": "text-cat-trabalho",
    },
  },
});

const Title: FC<TitleProps> = ({ id, level = 2, className, children }) => {
  const { size, titleColor } = useTitleHeading();
  const Tag = `h${level}` as const;

  return (
    <Tag id={id} className={cn(TITLE_VARIANTS({ size, color: titleColor }), className)}>
      {children}
    </Tag>
  );
};

type ActionProps = LinkProps;

const ACTION_VARIANTS = cva("ml-auto font-semibold", {
  variants: { layout: { desktop: "text-body-sm", mobile: "text-body-xs" } },
});

/** Call to action pushed to the end of the row. */
const Action: FC<ActionProps> = ({ className, ...linkProps }) => {
  const { layout } = useTitleHeading();

  return <Link {...linkProps} className={cn(ACTION_VARIANTS({ layout }), className)} />;
};

Root.displayName = "TitleHeading";
Marker.displayName = "TitleHeading.Marker";
Title.displayName = "TitleHeading.Title";
Action.displayName = "TitleHeading.Action";

export const TitleHeading = Object.assign(Root, { Marker, Title, Action });
